import { ExclamationTriangleIcon, LinkIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import React from 'react';

import {
  extractWizProperty,
  formatWizTimestamp,
} from '@/sections/poe/processors/wiz/helpers';
import type {
  WizParsedData,
  WizRenderContext,
} from '@/sections/poe/processors/wiz/types';

export function renderEndpointVuln(
  parsed: WizParsedData,
  ctx: WizRenderContext
): void {
  const { wizData, wizId } = parsed;
  const { sections, tempTabs, mainTabKeys } = ctx;

  if (
    wizData &&
    wizData['endpoint'] &&
    wizData['security tool finding'] &&
    wizData['vulnerability'] &&
    mainTabKeys.includes('DETECTION')
  ) {
    const endpoint = wizData['endpoint'];
    const securityTool = wizData['security tool finding'];
    const vulnerability = wizData['vulnerability'];

    const endpointName = extractWizProperty(endpoint, 'name') || '';
    const endpointPath = extractWizProperty(endpoint, 'path') || '';
    const httpStatus = extractWizProperty(endpoint, 'httpGETStatus') || '';
    const exposureLevel =
      extractWizProperty(endpoint, 'exposureLevel_name') || '';
    const exposureDescription =
      extractWizProperty(endpoint, 'exposureLevel_description') || '';
    const cloudPlatform = extractWizProperty(endpoint, 'cloudPlatform') || '';
    const protocols = extractWizProperty(endpoint, 'protocols') || '';

    const vulnName = extractWizProperty(vulnerability, 'name') || '';
    const cveId = extractWizProperty(vulnerability, 'cve') || '';
    const vulnIdentifier = cveId || vulnName;
    const isCve = /^CVE-\d{4}-\d+$/i.test(vulnIdentifier);
    const vulnDescription =
      extractWizProperty(vulnerability, 'description') || '';
    const severity =
      extractWizProperty(vulnerability, 'severity')?.replace(
        'VulnerabilitySeverity',
        ''
      ) || '';
    const publishedDate =
      extractWizProperty(vulnerability, 'publishedDate') || '';

    const detectedLibrary =
      extractWizProperty(securityTool, 'detectedByLibraryName') ||
      extractWizProperty(securityTool, 'detailed_name') ||
      '';
    const currentVersion =
      extractWizProperty(securityTool, 'detailed_version') || '';
    const fixedVersion =
      extractWizProperty(securityTool, 'detailed_fixedVersion') || '';
    const filePath =
      extractWizProperty(securityTool, 'detailed_filePath') ||
      extractWizProperty(securityTool, 'detectedByFilePath') ||
      '';
    const remediation = extractWizProperty(securityTool, 'remediation') || '';
    const language =
      extractWizProperty(securityTool, 'detailed_language') || '';

    sections['wiz_endpoint_vuln'] = (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* API Endpoint Information */}
          <div className="border-chariot-stroke rounded-lg border">
            <div className="border-chariot-stroke border-b px-4 py-3">
              <h4 className="text-chariot-text-primary flex items-center font-semibold">
                <LinkIcon className="text-chariot-brand-primary mr-2 size-4" />
                API Endpoint
              </h4>
            </div>
            <div className="space-y-3 p-4">
              <div>
                <span className="text-chariot-text-secondary mb-1 block text-xs font-medium">
                  URL:
                </span>
                <code className="bg-chariot-background-secondary block rounded px-2 py-1 text-xs break-all">
                  {endpointName}
                </code>
              </div>
              {endpointPath && (
                <div className="flex items-start justify-between py-1">
                  <span className="text-chariot-text-secondary text-xs font-medium">
                    Path:
                  </span>
                  <span className="text-chariot-text-primary font-mono text-xs">
                    {endpointPath}
                  </span>
                </div>
              )}
              {httpStatus && (
                <div className="flex items-start justify-between py-1">
                  <span className="text-chariot-text-secondary text-xs font-medium">
                    Status:
                  </span>
                  <span className="text-chariot-text-primary text-xs">
                    {httpStatus}
                  </span>
                </div>
              )}
              {exposureLevel && (
                <div className="flex items-start justify-between py-1">
                  <span className="text-chariot-text-secondary text-xs font-medium">
                    Exposure:
                  </span>
                  <span className="text-warning text-xs font-medium">
                    {exposureLevel}
                  </span>
                </div>
              )}
              {cloudPlatform && (
                <div className="flex items-start justify-between py-1">
                  <span className="text-chariot-text-secondary text-xs font-medium">
                    Platform:
                  </span>
                  <span className="text-chariot-text-primary text-xs">
                    {cloudPlatform}
                  </span>
                </div>
              )}
              {protocols && (
                <div className="flex items-start justify-between py-1">
                  <span className="text-chariot-text-secondary text-xs font-medium">
                    Protocol:
                  </span>
                  <span className="text-chariot-text-primary text-xs">
                    {protocols}
                  </span>
                </div>
              )}
              {exposureDescription && (
                <div className="border-chariot-stroke border-t pt-2">
                  <span className="text-chariot-text-secondary mb-1 block text-xs font-medium">
                    Exposure Details:
                  </span>
                  <p className="text-chariot-text-tertiary text-xs">
                    {exposureDescription}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Library Vulnerability */}
          <div className="border-chariot-stroke rounded-lg border">
            <div className="border-chariot-stroke border-b px-4 py-3">
              <h4 className="text-chariot-text-primary flex items-center font-semibold">
                <ExclamationTriangleIcon className="text-error mr-2 size-4" />
                Library Vulnerability
              </h4>
            </div>
            <div className="space-y-3 p-4">
              {vulnIdentifier && (
                <div>
                  <span className="text-chariot-text-secondary mb-1 block text-xs font-medium">
                    {isCve ? 'CVE ID:' : 'Vulnerability:'}
                  </span>
                  <span className="bg-error-selected text-error inline-flex items-center rounded px-2 py-1 text-xs font-medium">
                    {vulnIdentifier}
                  </span>
                </div>
              )}
              {detectedLibrary && (
                <div className="flex items-start justify-between py-1">
                  <span className="text-chariot-text-secondary text-xs font-medium">
                    Library:
                  </span>
                  <span className="text-chariot-text-primary font-mono text-xs">
                    {detectedLibrary}
                  </span>
                </div>
              )}
              {currentVersion && (
                <div>
                  <span className="text-chariot-text-secondary mb-1 block text-xs font-medium">
                    Current Version:
                  </span>
                  <code className="bg-error-selected text-error rounded px-2 py-1 text-xs">
                    {currentVersion}
                  </code>
                </div>
              )}
              {fixedVersion && (
                <div>
                  <span className="text-chariot-text-secondary mb-1 block text-xs font-medium">
                    Fixed Version:
                  </span>
                  <code className="bg-success-selected text-success rounded px-2 py-1 text-xs">
                    {fixedVersion}
                  </code>
                </div>
              )}
              {severity && (
                <div className="flex items-start justify-between py-1">
                  <span className="text-chariot-text-secondary text-xs font-medium">
                    Severity:
                  </span>
                  <span className="text-error text-xs font-bold">
                    {severity}
                  </span>
                </div>
              )}
              {language && (
                <div className="flex items-start justify-between py-1">
                  <span className="text-chariot-text-secondary text-xs font-medium">
                    Language:
                  </span>
                  <span className="text-chariot-text-primary text-xs">
                    {language}
                  </span>
                </div>
              )}
              {filePath && (
                <div>
                  <span className="text-chariot-text-secondary mb-1 block text-xs font-medium">
                    File Path:
                  </span>
                  <code className="bg-chariot-background-secondary block rounded px-2 py-1 text-xs break-all">
                    {filePath}
                  </code>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Vulnerability Description */}
        {vulnDescription && (
          <div className="border-chariot-stroke rounded-lg border p-4">
            <h5 className="text-chariot-text-primary mb-3 font-medium">
              Vulnerability Details
            </h5>
            <p className="text-chariot-text-secondary text-sm leading-relaxed">
              {vulnDescription}
            </p>
            {publishedDate && (
              <div className="border-chariot-stroke mt-3 border-t pt-3">
                <span className="text-chariot-text-secondary text-xs font-medium">
                  Published:
                </span>
                <span className="text-chariot-text-primary ml-2 text-xs">
                  {formatWizTimestamp(publishedDate)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Remediation */}
        {remediation && (
          <div className="border-success border-l-4 p-4">
            <div className="flex items-start">
              <CheckCircleIcon className="text-success mt-0.5 mr-3 size-5 shrink-0" />
              <div>
                <h5 className="text-chariot-text-primary mb-2 font-semibold">
                  Remediation
                </h5>
                <code className="bg-chariot-background-secondary block rounded px-3 py-2 text-sm">
                  {remediation}
                </code>
                {wizId && (
                  <div className="mt-3">
                    <span className="text-chariot-text-secondary text-xs font-medium">
                      Wiz ID:
                    </span>
                    <code className="bg-chariot-background-secondary ml-2 rounded px-2 py-1 text-xs">
                      {wizId}
                    </code>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
    tempTabs['DETECTION'].push({
      label: 'API Vulnerability',
      value: 'wiz_endpoint_vuln',
    });
  }
}
